type Service = 'segment' | 'mtag'

type EventConfig = {
  eventGroup: string
  eventName: string
  services: Service[]
  name: string
  description: string
  interaction_context: string
  interaction_element: string
  interaction_feature: string
  expected_properties: string[]
}

export const events: Record<string, EventConfig> = {
  // NOTE: not in AnalyticsInstrumentation values/mtag_click_event.json (spec has pairs_with,
  // not find_similar). Kept per ticket MB3ENG requirement; add upstream to make it canonical.
  product_card_find_similar_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_find_similar',
    services: ['segment', 'mtag'],
    name: 'Find Similar - Gallery View',
    description:
      "User clicks 'Find Similar' CTA on product card in gallery view.",
    interaction_context: 'product_card',
    interaction_element: 'find_similar',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_card_view_specs_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_view_specs',
    services: ['segment'],
    name: 'View Specs - Gallery View',
    description:
      "User clicks 'View Specs' CTA on product card in gallery view.",
    interaction_context: 'product_card',
    interaction_element: 'view_specs',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },

  product_card_brand_site_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_brand_site',
    services: ['segment'],
    name: 'Brand Site - Gallery View',
    description:
      "User clicks 'Brand Site' CTA on product card in gallery view.",
    interaction_context: 'product_card',
    interaction_element: 'brand_site',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_card_share_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_share_gallery',
    services: ['segment', 'mtag'],
    name: 'Share - Gallery View',
    description: "User clicks 'Share' CTA on product card in gallery view.",
    interaction_context: 'product_card',
    interaction_element: 'share',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  // NOTE: not in AnalyticsInstrumentation values/mtag_click_event.json; add upstream to make it canonical.
  product_card_get_pricing_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_get_pricing',
    services: ['segment'],
    name: 'Get Pricing - Gallery View',
    description:
      "User clicks 'Get pricing' CTA on product card in gallery view, which opens a pre-written pricing email to the brand rep.",
    interaction_context: 'product_card',
    interaction_element: 'get_pricing',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_card_contact_rep_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_contact_rep_gallery',
    services: ['segment'],
    name: 'Contact Rep - Gallery View',
    description:
      "User clicks 'Contact [Rep Name]' CTA on product card in gallery view.",
    interaction_context: 'product_card',
    interaction_element: 'contact_rep',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_card_color_swatch_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_color_swatch',
    services: ['segment'],
    name: 'Color Swatch - Gallery View',
    description:
      'User clicks a color swatch on product card in gallery view to change the product variant.',
    interaction_context: 'product_card',
    interaction_element: 'color_swatch',
    interaction_feature: 'gallery_view',
    expected_properties: [
      'product_id',
      'product_name',
      'brand',
      'variant',
      'url',
    ],
  },
  product_card_photo_carousel_swipe_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_swipe_photo_carousel',
    services: ['segment'],
    name: 'Photo Carousel - Gallery View',
    description:
      'User swipes through the photo carousel on product card in gallery view.',
    interaction_context: 'product_card',
    interaction_element: 'photo_carousel_swipe',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_card_photo_carousel_open_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_open_photo_enlarge',
    services: ['segment'],
    name: 'Photo Enlarge - Gallery View',
    description: 'User clicks on a product image to open it in gallery view.',
    interaction_context: 'product_card',
    interaction_element: 'photo_carousel_open',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_card_photo_carousel_close_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_close_photo_enlarge',
    services: ['segment'],
    name: 'Photo Carousel Close - Gallery View',
    description:
      'User clicks close to exit the enlarged photo experience in gallery view.',
    interaction_context: 'product_card',
    interaction_element: 'photo_carousel_close',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_card_material_colorways_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_material_colorways',
    services: ['segment'],
    name: 'Material Colorways - Gallery View',
    description:
      "User clicks 'Colorways' CTA on product card in gallery view to open the colorways sheet.",
    interaction_context: 'product_card',
    interaction_element: 'colorways',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },

  popup_modal_find_similar_already_sampled_interstitial: {
    eventGroup: 'user',
    eventName: 'user_click_find_similar_sampled',
    services: ['segment'],
    name: 'Find Similar - Already Sampled',
    description:
      "User clicks 'Find Similar' on the already-sampled interstitial.",
    interaction_context: 'popup_modal',
    interaction_element: 'find_similar',
    interaction_feature: 'already_sampled_interstitial',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_configuration_close_colorways_sheet: {
    eventGroup: 'user',
    eventName: 'user_click_close_colorways',
    services: ['segment'],
    name: 'Close - Colorways',
    description: 'User closes the colorways sheet.',
    interaction_context: 'product_configuration',
    interaction_element: 'close',
    interaction_feature: 'colorways_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  header_open_sign_in_modal_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_open_sign_in',
    services: ['segment'],
    name: 'Open Sign In Modal - Gallery View',
    description:
      'User clicks the header profile button to open the sign-in modal.',
    interaction_context: 'header',
    interaction_element: 'open_sign_in_modal',
    interaction_feature: 'gallery_view',
    expected_properties: ['url'],
  },
  // NOTE: not in AnalyticsInstrumentation values/mtag_click_event.json; add upstream to make it canonical.
  header_open_project_page_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_open_project_page',
    services: ['segment'],
    name: 'Open Project Page - Gallery View',
    description:
      'User clicks the header project pill to open the project page on MB3.',
    interaction_context: 'header',
    interaction_element: 'open_project_page',
    interaction_feature: 'gallery_view',
    expected_properties: ['url'],
  },
  product_card_open_approval_status_gallery_view: {
    eventGroup: 'user',
    eventName: 'user_click_open_approval_status',
    services: ['segment'],
    name: 'Open Approval Status - Gallery View',
    description: 'User opens the approval status dialog on the product card.',
    interaction_context: 'product_card',
    interaction_element: 'open_approval_status',
    interaction_feature: 'gallery_view',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  specs_page_see_more_view_specs_modal: {
    eventGroup: 'user',
    eventName: 'user_click_see_more_specs',
    services: ['segment'],
    name: 'See More - View Specs',
    description: 'User expands a truncated spec value via See More.',
    interaction_context: 'specs_page',
    interaction_element: 'see_more',
    interaction_feature: 'view_specs_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  popup_modal_sign_in_sign_in_modal: {
    eventGroup: 'user',
    eventName: 'user_click_sign_in',
    services: ['segment', 'mtag'],
    name: 'Sign In - Sign In Modal',
    description: "User clicks 'Sign into Materia' CTA on the sign-in modal.",
    interaction_context: 'popup_modal',
    interaction_element: 'sign_in',
    interaction_feature: 'sign_in_modal',
    expected_properties: ['url'],
  },
  selection_list_specialty_select_create_account: {
    eventGroup: 'user',
    eventName: 'user_select_specialty',
    services: ['segment'],
    name: 'Specialty Select - Create Account',
    description:
      'User selects a primary specialty during the account creation flow.',
    interaction_context: 'selection_list',
    interaction_element: 'specialty_select',
    interaction_feature: 'create_account',
    expected_properties: ['specialty', 'url'],
  },
  selection_list_additional_specialty_select_create_account: {
    eventGroup: 'user',
    eventName: 'user_select_additional_specialty',
    services: ['segment'],
    name: 'Additional Specialty Select - Create Account',
    description:
      'User selects an additional specialty during the account creation flow.',
    interaction_context: 'selection_list',
    interaction_element: 'additional_specialty_select',
    interaction_feature: 'create_account',
    expected_properties: ['specialty', 'url'],
  },
  promo_video_skip_video_create_account: {
    eventGroup: 'user',
    eventName: 'user_skip_promo_video',
    services: ['segment'],
    name: 'Skip Video - Create Account',
    description:
      "User clicks 'Skip video' CTA on the promotional warehouse video screen.",
    interaction_context: 'promo_video',
    interaction_element: 'skip_video',
    interaction_feature: 'create_account',
    expected_properties: ['url'],
  },
  welcome_screen_build_your_first_box_create_account: {
    eventGroup: 'user',
    eventName: 'user_click_build_first_box',
    services: ['segment'],
    name: 'Build Your First Box - Create Account',
    description:
      "User clicks 'Build your first box' CTA on the welcome screen.",
    interaction_context: 'welcome_screen',
    interaction_element: 'build_your_first_box',
    interaction_feature: 'create_account',
    expected_properties: ['url'],
  },
  added_to_cart_confirmation_go_to_cart_cart: {
    eventGroup: 'user',
    eventName: 'user_click_go_to_cart',
    services: ['segment'],
    name: 'Go To Cart - Added To Cart',
    description:
      "User clicks 'Go to cart' CTA on the added-to-cart confirmation screen.",
    interaction_context: 'added_to_cart_confirmation',
    interaction_element: 'go_to_cart',
    interaction_feature: 'cart',
    expected_properties: ['url'],
  },
  checkout_page_place_order_checkout: {
    eventGroup: 'user',
    eventName: 'user_create_order',
    services: ['segment', 'mtag'],
    name: 'Place Order - Checkout',
    description:
      "User clicks 'Place order' CTA on the checkout page. The order_completed event fires separately after backend confirmation.",
    interaction_context: 'checkout_page',
    interaction_element: 'place_order',
    interaction_feature: 'checkout',
    expected_properties: ['url'],
  },
  popup_modal_lead_time_contact_rep_modal: {
    eventGroup: 'user',
    eventName: 'user_select_lead_time',
    services: ['segment'],
    name: 'Lead Time - Contact Rep Modal',
    description:
      "User taps 'Lead time and availability' in the contact rep sheet, which opens a pre-written email.",
    interaction_context: 'popup_modal',
    interaction_element: 'lead_time',
    interaction_feature: 'contact_rep_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  popup_modal_pricing_quote_contact_rep_modal: {
    eventGroup: 'user',
    eventName: 'user_select_pricing_quote',
    services: ['segment'],
    name: 'Pricing Quote - Contact Rep Modal',
    description:
      "User taps 'Pricing or a quote' in the contact rep sheet, which opens a pre-written email.",
    interaction_context: 'popup_modal',
    interaction_element: 'pricing_quote',
    interaction_feature: 'contact_rep_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  // NOTE: not in the upstream mtag interaction catalog yet. It rides impression_event, whose
  // impression_type is the analyticsId, so it validates; add it upstream to make it canonical.
  popup_modal_larger_sample_contact_rep_modal: {
    eventGroup: 'user',
    eventName: 'user_select_larger_sample',
    services: ['segment'],
    name: 'Larger Sample - Contact Rep Modal',
    description:
      "User taps 'Larger sample' in the contact rep sheet, which opens a pre-written email.",
    interaction_context: 'popup_modal',
    interaction_element: 'larger_sample',
    interaction_feature: 'contact_rep_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  popup_modal_something_else_contact_rep_modal: {
    eventGroup: 'user',
    eventName: 'user_select_something_else',
    services: ['segment'],
    name: 'Something Else - Contact Rep Modal',
    description:
      "User taps 'Something else' in the contact rep sheet, which opens a pre-written email.",
    interaction_context: 'popup_modal',
    interaction_element: 'something_else',
    interaction_feature: 'contact_rep_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  popup_modal_text_rep_contact_rep_modal: {
    eventGroup: 'user',
    eventName: 'user_click_text_rep',
    services: ['segment'],
    name: 'Text Rep - Contact Rep Modal',
    description:
      "User taps 'Text' under 'Prefer to talk?' in the contact rep sheet.",
    interaction_context: 'popup_modal',
    interaction_element: 'text_rep',
    interaction_feature: 'contact_rep_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  popup_modal_call_rep_contact_rep_modal: {
    eventGroup: 'user',
    eventName: 'user_click_call_rep',
    services: ['segment'],
    name: 'Call Rep - Contact Rep Modal',
    description:
      "User taps 'Call' under 'Prefer to talk?' in the contact rep sheet.",
    interaction_context: 'popup_modal',
    interaction_element: 'call_rep',
    interaction_feature: 'contact_rep_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  popup_modal_review_in_email_contact_rep_modal: {
    eventGroup: 'user',
    eventName: 'user_click_review_in_email',
    services: ['segment', 'mtag'],
    name: 'Review In Email - Contact Rep Modal',
    description:
      'User hands off to their email, phone or messages app from the contact rep sheet (a topic, Call or Text). Fired programmatically; the tag service counts it as a rep contact on the brand dashboard.',
    interaction_context: 'popup_modal',
    interaction_element: 'review_in_email',
    interaction_feature: 'contact_rep_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  specs_page_view_on_brand_site_view_specs_modal: {
    eventGroup: 'user',
    eventName: 'user_click_view_on_brand_site',
    services: ['segment'],
    name: 'View On Brand Site - View Specs',
    description:
      "User clicks 'View on [Brand Name] site' CTA on the view specs screen.",
    interaction_context: 'specs_page',
    interaction_element: 'view_on_brand_site',
    interaction_feature: 'view_specs_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  specs_page_share_view_specs_modal: {
    eventGroup: 'user',
    eventName: 'user_click_share_specs',
    services: ['segment', 'mtag'],
    name: 'Share - View Specs',
    description: 'User clicks the share button on the view specs screen.',
    interaction_context: 'specs_page',
    interaction_element: 'share',
    interaction_feature: 'view_specs_modal',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },

  search_bar_search_brand_home: {
    eventGroup: 'user',
    eventName: 'user_search_brand_home',
    services: ['segment'],
    name: 'Search - Brand Home',
    description:
      'Brand user submits a search query (ZIP code, location, rep, firm, etc.) on the home screen.',
    interaction_context: 'search_bar',
    interaction_element: 'search',
    interaction_feature: 'brand_home',
    expected_properties: ['selected_value', 'url'],
  },
  filter_bar_time_filter_select_brand_home: {
    eventGroup: 'user',
    eventName: 'user_select_time_filter',
    services: ['segment'],
    name: 'Time Filter Select - Brand Home',
    description:
      'Brand user selects a time period filter (Today, 7 days, 30 days, 3 months) on the home screen.',
    interaction_context: 'filter_bar',
    interaction_element: 'time_filter_select',
    interaction_feature: 'brand_home',
    expected_properties: ['selected_value', 'url'],
  },
  map_map_status_filter_brand_home: {
    eventGroup: 'user',
    eventName: 'user_filter_map_status',
    services: ['segment'],
    name: 'Map Status Filter - Brand Home',
    description:
      'Brand user filters the map by tag status (All, Active, Inactive) on the home screen.',
    interaction_context: 'map',
    interaction_element: 'map_status_filter',
    interaction_feature: 'brand_home',
    expected_properties: ['selected_value', 'url'],
  },
  map_map_zoom_in_brand_home: {
    eventGroup: 'user',
    eventName: 'user_zoom_in_map',
    services: ['segment'],
    name: 'Map Zoom In - Brand Home',
    description: 'Brand user zooms in on the map on the home screen.',
    interaction_context: 'map',
    interaction_element: 'map_zoom_in',
    interaction_feature: 'brand_home',
    expected_properties: ['url'],
  },
  map_map_zoom_out_brand_home: {
    eventGroup: 'user',
    eventName: 'user_zoom_out_map',
    services: ['segment'],
    name: 'Map Zoom Out - Brand Home',
    description: 'Brand user zooms out on the map on the home screen.',
    interaction_context: 'map',
    interaction_element: 'map_zoom_out',
    interaction_feature: 'brand_home',
    expected_properties: ['url'],
  },
  map_map_expand_brand_home: {
    eventGroup: 'user',
    eventName: 'user_expand_map',
    services: ['segment'],
    name: 'Map Expand - Brand Home',
    description:
      'Brand user expands the map to full screen on the home screen.',
    interaction_context: 'map',
    interaction_element: 'map_expand',
    interaction_feature: 'brand_home',
    expected_properties: ['url'],
  },
  most_engaged_products_sort_select_brand_home: {
    eventGroup: 'user',
    eventName: 'user_sort_most_engaged_products',
    services: ['segment'],
    name: 'Sort Select - Brand Home',
    description:
      "Brand user changes the sort option on the 'Most engaged products' section on the home screen.",
    interaction_context: 'most_engaged_products',
    interaction_element: 'sort_select',
    interaction_feature: 'brand_home',
    expected_properties: ['selected_value', 'url'],
  },
  settings_nav_tab_select_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_select_settings_tab',
    services: ['segment'],
    name: 'Settings Tab Select - Brand Settings',
    description:
      'Brand user selects a tab (Products, WebApp) in the settings navigation.',
    interaction_context: 'settings_nav',
    interaction_element: 'tab_select',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['selected_value', 'url'],
  },
  product_retirement_retirement_option_select_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_select_retirement_option',
    services: ['segment'],
    name: 'Retirement Option Select - Brand Settings',
    description:
      'Brand user selects a retirement option (Forward to Brand Page or Custom Redirection).',
    interaction_context: 'product_retirement',
    interaction_element: 'retirement_option_select',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['selected_value', 'url'],
  },
  product_list_filter_tab_select_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_select_product_filter_tab',
    services: ['segment'],
    name: 'Filter Tab Select - Brand Settings',
    description:
      'Brand user selects a filter tab (All, New SKU, MB Brand Page) on the product replacement list.',
    interaction_context: 'product_list',
    interaction_element: 'filter_tab_select',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['selected_value', 'url'],
  },
  product_list_search_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_search_product_list',
    services: ['segment'],
    name: 'Search Products - Brand Settings',
    description: 'Brand user searches within the product replacement list.',
    interaction_context: 'product_list',
    interaction_element: 'search',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['selected_value', 'url'],
  },
  product_list_product_select_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_select_product_checkbox',
    services: ['segment'],
    name: 'Product Select - Brand Settings',
    description:
      'Brand user selects a product checkbox in the replacement list.',
    interaction_context: 'product_list',
    interaction_element: 'product_select',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_list_bulk_replacement_select_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_select_bulk_replacement',
    services: ['segment'],
    name: 'Bulk Replacement Select - Brand Settings',
    description:
      'Brand user selects a bulk replacement type for selected products.',
    interaction_context: 'product_list',
    interaction_element: 'bulk_replacement_select',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['selected_value', 'url'],
  },
  product_row_replacement_select_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_select_individual_replacement',
    services: ['segment'],
    name: 'Individual Replacement Select - Brand Settings',
    description:
      'Brand user changes the replacement type for a single product.',
    interaction_context: 'product_row',
    interaction_element: 'replacement_select',
    interaction_feature: 'brand_product_retirement',
    expected_properties: [
      'product_id',
      'product_name',
      'brand',
      'selected_value',
      'url',
    ],
  },
  replacement_picker_search_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_search_replacement_product',
    services: ['segment'],
    name: 'Search Replacement Product - Brand Settings',
    description:
      'Brand user searches for a replacement product in the product picker.',
    interaction_context: 'replacement_picker',
    interaction_element: 'search',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['selected_value', 'url'],
  },
  replacement_picker_apply_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_apply_replacement',
    services: ['segment'],
    name: 'Apply Replacement - Brand Settings',
    description:
      "Brand user clicks 'Apply' to confirm a replacement product selection.",
    interaction_context: 'replacement_picker',
    interaction_element: 'apply',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_row_remove_replacement_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_remove_replacement',
    services: ['segment'],
    name: 'Remove Replacement - Brand Settings',
    description:
      'Brand user removes a replacement product assignment from a product row.',
    interaction_context: 'product_row',
    interaction_element: 'remove_replacement',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['product_id', 'product_name', 'brand', 'url'],
  },
  product_list_column_sort_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_sort_product_column',
    services: ['segment'],
    name: 'Column Sort - Brand Settings',
    description: 'Brand user sorts the product list by a column header.',
    interaction_context: 'product_list',
    interaction_element: 'column_sort',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['selected_value', 'url'],
  },
  product_list_page_select_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_select_product_list_page',
    services: ['segment'],
    name: 'Page Select - Brand Settings',
    description:
      'Brand user navigates to a page in the product list pagination.',
    interaction_context: 'product_list',
    interaction_element: 'page_select',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['selected_value', 'url'],
  },
  product_retirement_save_brand_product_retirement: {
    eventGroup: 'user',
    eventName: 'user_save_product_retirement',
    services: ['segment'],
    name: 'Save - Brand Settings',
    description:
      "Brand user clicks 'Save' to persist product retirement configuration. A separate brand_product_retirement_saved event fires with the full batch summary.",
    interaction_context: 'product_retirement',
    interaction_element: 'save',
    interaction_feature: 'brand_product_retirement',
    expected_properties: ['url'],
  },
  contact_preferences_recipient_select_brand_contact_preferences: {
    eventGroup: 'user',
    eventName: 'user_select_contact_recipient',
    services: ['segment'],
    name: 'Recipient Select - Brand Contact Preferences',
    description:
      'Brand user selects a recipient type for contact requests (Default rep or General Contact inbox).',
    interaction_context: 'contact_preferences',
    interaction_element: 'recipient_select',
    interaction_feature: 'brand_contact_preferences',
    expected_properties: ['selected_value', 'url'],
  },
  contact_options_lead_time_toggle_brand_contact_preferences: {
    eventGroup: 'user',
    eventName: 'user_toggle_lead_time',
    services: ['segment'],
    name: 'Lead Time Toggle - Brand Contact Preferences',
    description: "Brand user toggles the 'Lead time' contact option on or off.",
    interaction_context: 'contact_options',
    interaction_element: 'lead_time_toggle',
    interaction_feature: 'brand_contact_preferences',
    expected_properties: ['selected_value', 'url'],
  },
  contact_options_pricing_quote_toggle_brand_contact_preferences: {
    eventGroup: 'user',
    eventName: 'user_toggle_pricing_quote',
    services: ['segment'],
    name: 'Pricing Quote Toggle - Brand Contact Preferences',
    description:
      "Brand user toggles the 'Pricing / quote' contact option on or off.",
    interaction_context: 'contact_options',
    interaction_element: 'pricing_quote_toggle',
    interaction_feature: 'brand_contact_preferences',
    expected_properties: ['selected_value', 'url'],
  },
  contact_options_something_else_toggle_brand_contact_preferences: {
    eventGroup: 'user',
    eventName: 'user_toggle_something_else',
    services: ['segment'],
    name: 'Something Else Toggle - Brand Contact Preferences',
    description:
      "Brand user toggles the 'Something else' contact option on or off.",
    interaction_context: 'contact_options',
    interaction_element: 'something_else_toggle',
    interaction_feature: 'brand_contact_preferences',
    expected_properties: ['selected_value', 'url'],
  },
  contact_options_text_toggle_brand_contact_preferences: {
    eventGroup: 'user',
    eventName: 'user_toggle_text_option',
    services: ['segment'],
    name: 'Text Toggle - Brand Contact Preferences',
    description: "Brand user toggles the 'Text' contact option on or off.",
    interaction_context: 'contact_options',
    interaction_element: 'text_toggle',
    interaction_feature: 'brand_contact_preferences',
    expected_properties: ['selected_value', 'url'],
  },
  contact_options_call_toggle_brand_contact_preferences: {
    eventGroup: 'user',
    eventName: 'user_toggle_call_option',
    services: ['segment'],
    name: 'Call Toggle - Brand Contact Preferences',
    description: "Brand user toggles the 'Call' contact option on or off.",
    interaction_context: 'contact_options',
    interaction_element: 'call_toggle',
    interaction_feature: 'brand_contact_preferences',
    expected_properties: ['selected_value', 'url'],
  },
  contact_preferences_save_brand_contact_preferences: {
    eventGroup: 'user',
    eventName: 'user_save_contact_preferences',
    services: ['segment'],
    name: 'Save - Brand Contact Preferences',
    description:
      "Brand user clicks 'Save' to persist contact preference configuration. A separate brand_contact_preferences_saved event fires with the full configuration state.",
    interaction_context: 'contact_preferences',
    interaction_element: 'save',
    interaction_feature: 'brand_contact_preferences',
    expected_properties: ['url'],
  },
} as const

//TODO: Create a mapping script
export const originalEvents = {
  description:
    'Valid interaction combinations for mtag_click_event. The expected_properties array lists dynamic properties that should be passed as top-level event properties alongside the interaction fields. See events/mtag_click_event.json for expected payload structure.',
  interactions: [
    {
      name: 'Find Similar - Gallery View',
      description:
        "User clicks 'Find Similar' CTA on product card in gallery view.",
      interaction_context: 'product_card',
      interaction_element: 'find_similar',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'View Specs - Gallery View',
      description:
        "User clicks 'View Specs' CTA on product card in gallery view.",
      interaction_context: 'product_card',
      interaction_element: 'view_specs',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Material Options - Gallery View',
      description:
        "User clicks 'Material Options' CTA on product card in gallery view.",
      interaction_context: 'product_card',
      interaction_element: 'material_options',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Brand Site - Gallery View',
      description:
        "User clicks 'Brand Site' CTA on product card in gallery view.",
      interaction_context: 'product_card',
      interaction_element: 'brand_site',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Share - Gallery View',
      description: "User clicks 'Share' CTA on product card in gallery view.",
      interaction_context: 'product_card',
      interaction_element: 'share',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Contact Rep - Gallery View',
      description:
        "User clicks 'Contact [Rep Name]' CTA on product card in gallery view.",
      interaction_context: 'product_card',
      interaction_element: 'contact_rep',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Color Swatch - Gallery View',
      description:
        'User clicks a color swatch on product card in gallery view to change the product variant.',
      interaction_context: 'product_card',
      interaction_element: 'color_swatch',
      interaction_feature: 'gallery_view',
      expected_properties: [
        'product_id',
        'product_name',
        'brand',
        'variant',
        'url',
      ],
    },
    {
      name: 'Photo Carousel - Gallery View',
      description:
        'User swipes through the photo carousel on product card in gallery view.',
      interaction_context: 'product_card',
      interaction_element: 'photo_carousel_swipe',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Photo Enlarge - Gallery View',
      description: 'User clicks on a product image to open it in gallery view.',
      interaction_context: 'product_card',
      interaction_element: 'photo_carousel_open',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Photo Carousel Close - Gallery View',
      description:
        'User clicks close to exit the enlarged photo experience in gallery view.',
      interaction_context: 'product_card',
      interaction_element: 'photo_carousel_close',
      interaction_feature: 'gallery_view',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Sign In - Sign In Modal',
      description: "User clicks 'Sign into Materia' CTA on the sign-in modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'sign_in',
      interaction_feature: 'sign_in_modal',
      expected_properties: ['url'],
    },
    {
      name: 'Create Account - Sign In Modal',
      description: "User clicks 'Create an account' CTA on the sign-in modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'create_account',
      interaction_feature: 'sign_in_modal',
      expected_properties: ['url'],
    },
    {
      name: 'Specialty Select - Create Account',
      description:
        'User selects a primary specialty during the account creation flow.',
      interaction_context: 'selection_list',
      interaction_element: 'specialty_select',
      interaction_feature: 'create_account',
      selected_values: 'dynamic — specialty value selected by user',
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Additional Specialty Select - Create Account',
      description:
        'User selects an additional specialty during the account creation flow.',
      interaction_context: 'selection_list',
      interaction_element: 'additional_specialty_select',
      interaction_feature: 'create_account',
      selected_values: 'dynamic — specialty value selected by user',
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Skip Video - Create Account',
      description:
        "User clicks 'Skip video' CTA on the promotional warehouse video screen.",
      interaction_context: 'promo_video',
      interaction_element: 'skip_video',
      interaction_feature: 'create_account',
      expected_properties: ['url'],
    },
    {
      name: 'Build Your First Box - Create Account',
      description:
        "User clicks 'Build your first box' CTA on the welcome screen.",
      interaction_context: 'welcome_screen',
      interaction_element: 'build_your_first_box',
      interaction_feature: 'create_account',
      expected_properties: ['url'],
    },
    {
      name: 'Go To Cart - Added To Cart',
      description:
        "User clicks 'Go to cart' CTA on the added-to-cart confirmation screen.",
      interaction_context: 'added_to_cart_confirmation',
      interaction_element: 'go_to_cart',
      interaction_feature: 'cart',
      cart_id: 'required',
      expected_properties: ['url'],
    },
    {
      name: 'Place Order - Checkout',
      description:
        "User clicks 'Place order' CTA on the checkout page. The order_completed event fires separately after backend confirmation.",
      interaction_context: 'checkout_page',
      interaction_element: 'place_order',
      interaction_feature: 'checkout',
      cart_id: 'required',
      expected_properties: ['url'],
    },
    {
      name: 'Lead Time - Contact Rep Modal',
      description: "User selects 'Lead time' option in the contact rep modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'lead_time',
      interaction_feature: 'contact_rep_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Pricing Quote - Contact Rep Modal',
      description:
        "User selects 'Pricing / quote' option in the contact rep modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'pricing_quote',
      interaction_feature: 'contact_rep_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Something Else - Contact Rep Modal',
      description:
        "User selects 'Something else' option in the contact rep modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'something_else',
      interaction_feature: 'contact_rep_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Text Rep - Contact Rep Modal',
      description:
        "User selects 'Text [Rep Name]' option in the contact rep modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'text_rep',
      interaction_feature: 'contact_rep_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Call Rep - Contact Rep Modal',
      description:
        "User selects 'Call [Rep Name]' option in the contact rep modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'call_rep',
      interaction_feature: 'contact_rep_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Review In Email - Contact Rep Modal',
      description:
        "User clicks 'Review in your email app' CTA to submit the contact rep request.",
      interaction_context: 'popup_modal',
      interaction_element: 'review_in_email',
      interaction_feature: 'contact_rep_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Core Specs Tab - View Specs',
      description:
        "User selects the 'Core Specs' filter tab on the view specs screen.",
      interaction_context: 'filter_tab',
      interaction_element: 'core_specs',
      interaction_feature: 'view_specs_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Sustainability Tab - View Specs',
      description:
        "User selects the 'Sustainability' filter tab on the view specs screen.",
      interaction_context: 'filter_tab',
      interaction_element: 'sustainability',
      interaction_feature: 'view_specs_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Documents Tab - View Specs',
      description:
        "User selects the 'Documents' filter tab on the view specs screen.",
      interaction_context: 'filter_tab',
      interaction_element: 'documents',
      interaction_feature: 'view_specs_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Send To My Email - View Specs',
      description:
        "User clicks 'Send to my email' CTA on the view specs screen.",
      interaction_context: 'specs_page',
      interaction_element: 'send_to_email',
      interaction_feature: 'view_specs_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'View On Brand Site - View Specs',
      description:
        "User clicks 'View on [Brand Name] site' CTA on the view specs screen.",
      interaction_context: 'specs_page',
      interaction_element: 'view_on_brand_site',
      interaction_feature: 'view_specs_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Share - View Specs',
      description: 'User clicks the share button on the view specs screen.',
      interaction_context: 'specs_page',
      interaction_element: 'share',
      interaction_feature: 'view_specs_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Colorway Select - Material Options',
      description: 'User selects a colorway on the material options screen.',
      interaction_context: 'product_configuration',
      interaction_element: 'colorway_select',
      interaction_feature: 'material_options_modal',
      expected_properties: [
        'product_id',
        'product_name',
        'brand',
        'selected_value',
        'url',
      ],
    },
    {
      name: 'Finish Select - Material Options',
      description:
        'User selects a finish option on the material options screen.',
      interaction_context: 'product_configuration',
      interaction_element: 'finish_select',
      interaction_feature: 'material_options_modal',
      expected_properties: [
        'product_id',
        'product_name',
        'brand',
        'selected_value',
        'url',
      ],
    },
    {
      name: 'Size Select - Material Options',
      description: 'User selects a size option on the material options screen.',
      interaction_context: 'product_configuration',
      interaction_element: 'size_select',
      interaction_feature: 'material_options_modal',
      expected_properties: [
        'product_id',
        'product_name',
        'brand',
        'selected_value',
        'url',
      ],
    },
    {
      name: 'Thickness Select - Material Options',
      description:
        'User selects a thickness option on the material options screen.',
      interaction_context: 'product_configuration',
      interaction_element: 'thickness_select',
      interaction_feature: 'material_options_modal',
      expected_properties: [
        'product_id',
        'product_name',
        'brand',
        'selected_value',
        'url',
      ],
    },
    {
      name: 'Share - Material Options',
      description:
        'User clicks the share button on the material options screen.',
      interaction_context: 'product_configuration',
      interaction_element: 'share',
      interaction_feature: 'material_options_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Contact Rep Order Sample - Material Options',
      description:
        "User clicks 'Contact Rep to order sample' CTA on the material options screen.",
      interaction_context: 'product_configuration',
      interaction_element: 'contact_rep_order_sample',
      interaction_feature: 'material_options_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Select Available Sample - Material Options',
      description:
        "User clicks 'Select available overnight sample' CTA on the material options screen.",
      interaction_context: 'product_configuration',
      interaction_element: 'select_available_sample',
      interaction_feature: 'material_options_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Better Performance - Find Similar Modal',
      description:
        "User selects 'Better performance' preference in the find similar modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'better_performance',
      interaction_feature: 'find_similar_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Greater Durability - Find Similar Modal',
      description:
        "User selects 'Greater durability' preference in the find similar modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'greater_durability',
      interaction_feature: 'find_similar_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Similar Look And Feel - Find Similar Modal',
      description:
        "User selects 'Similar look & feel' preference in the find similar modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'similar_look_and_feel',
      interaction_feature: 'find_similar_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'More Affordable - Find Similar Modal',
      description:
        "User selects 'More affordable' preference in the find similar modal.",
      interaction_context: 'popup_modal',
      interaction_element: 'more_affordable',
      interaction_feature: 'find_similar_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Find Similar Submit - Find Similar Modal',
      description:
        "User clicks 'Find similar' CTA to submit preferences and start the search.",
      interaction_context: 'popup_modal',
      interaction_element: 'find_similar_submit',
      interaction_feature: 'find_similar_modal',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Go To Cart - Find Similar Results',
      description:
        "User clicks 'Go to cart' CTA on the find similar results page.",
      interaction_context: 'find_similar_results',
      interaction_element: 'go_to_cart',
      interaction_feature: 'find_similar_results',
      expected_properties: ['cart_id', 'url'],
    },
    {
      name: 'Search - Brand Home',
      description:
        'Brand user submits a search query (ZIP code, location, rep, firm, etc.) on the home screen.',
      interaction_context: 'search_bar',
      interaction_element: 'search',
      interaction_feature: 'brand_home',
      selected_values: 'dynamic — free-form search query',
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Time Filter Select - Brand Home',
      description:
        'Brand user selects a time period filter (Today, 7 days, 30 days, 3 months) on the home screen.',
      interaction_context: 'filter_bar',
      interaction_element: 'time_filter_select',
      interaction_feature: 'brand_home',
      selected_values: ['today', '7_days', '30_days', '3_months'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Map Status Filter - Brand Home',
      description:
        'Brand user filters the map by tag status (All, Active, Inactive) on the home screen.',
      interaction_context: 'map',
      interaction_element: 'map_status_filter',
      interaction_feature: 'brand_home',
      selected_values: ['all', 'active', 'inactive'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Map Zoom In - Brand Home',
      description: 'Brand user zooms in on the map on the home screen.',
      interaction_context: 'map',
      interaction_element: 'map_zoom_in',
      interaction_feature: 'brand_home',
      expected_properties: ['url'],
    },
    {
      name: 'Map Zoom Out - Brand Home',
      description: 'Brand user zooms out on the map on the home screen.',
      interaction_context: 'map',
      interaction_element: 'map_zoom_out',
      interaction_feature: 'brand_home',
      expected_properties: ['url'],
    },
    {
      name: 'Map Expand - Brand Home',
      description:
        'Brand user expands the map to full screen on the home screen.',
      interaction_context: 'map',
      interaction_element: 'map_expand',
      interaction_feature: 'brand_home',
      expected_properties: ['url'],
    },
    {
      name: 'Sort Select - Brand Home',
      description:
        "Brand user changes the sort option on the 'Most engaged products' section on the home screen.",
      interaction_context: 'most_engaged_products',
      interaction_element: 'sort_select',
      interaction_feature: 'brand_home',
      selected_values: 'dynamic — sort options TBD',
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Settings Tab Select - Brand Settings',
      description:
        'Brand user selects a tab (Products, WebApp) in the settings navigation.',
      interaction_context: 'settings_nav',
      interaction_element: 'tab_select',
      interaction_feature: 'brand_product_retirement',
      selected_values: ['products', 'webapp'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Retirement Option Select - Brand Settings',
      description:
        'Brand user selects a retirement option (Forward to Brand Page or Custom Redirection).',
      interaction_context: 'product_retirement',
      interaction_element: 'retirement_option_select',
      interaction_feature: 'brand_product_retirement',
      selected_values: ['forward_to_brand_page', 'custom_redirection'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Filter Tab Select - Brand Settings',
      description:
        'Brand user selects a filter tab (All, New SKU, MB Brand Page) on the product replacement list.',
      interaction_context: 'product_list',
      interaction_element: 'filter_tab_select',
      interaction_feature: 'brand_product_retirement',
      selected_values: ['all', 'new_sku', 'mb_brand_page'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Search Products - Brand Settings',
      description: 'Brand user searches within the product replacement list.',
      interaction_context: 'product_list',
      interaction_element: 'search',
      interaction_feature: 'brand_product_retirement',
      selected_values: 'dynamic — free-form search query',
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Product Select - Brand Settings',
      description:
        'Brand user selects a product checkbox in the replacement list.',
      interaction_context: 'product_list',
      interaction_element: 'product_select',
      interaction_feature: 'brand_product_retirement',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Bulk Replacement Select - Brand Settings',
      description:
        'Brand user selects a bulk replacement type for selected products.',
      interaction_context: 'product_list',
      interaction_element: 'bulk_replacement_select',
      interaction_feature: 'brand_product_retirement',
      selected_values: ['mb_brand_page', 'new_sku'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Individual Replacement Select - Brand Settings',
      description:
        'Brand user changes the replacement type for a single product.',
      interaction_context: 'product_row',
      interaction_element: 'replacement_select',
      interaction_feature: 'brand_product_retirement',
      selected_values: ['mb_brand_page', 'new_sku'],
      expected_properties: [
        'product_id',
        'product_name',
        'brand',
        'selected_value',
        'url',
      ],
    },
    {
      name: 'Search Replacement Product - Brand Settings',
      description:
        'Brand user searches for a replacement product in the product picker.',
      interaction_context: 'replacement_picker',
      interaction_element: 'search',
      interaction_feature: 'brand_product_retirement',
      selected_values: 'dynamic — free-form search query',
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Apply Replacement - Brand Settings',
      description:
        "Brand user clicks 'Apply' to confirm a replacement product selection.",
      interaction_context: 'replacement_picker',
      interaction_element: 'apply',
      interaction_feature: 'brand_product_retirement',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Remove Replacement - Brand Settings',
      description:
        'Brand user removes a replacement product assignment from a product row.',
      interaction_context: 'product_row',
      interaction_element: 'remove_replacement',
      interaction_feature: 'brand_product_retirement',
      expected_properties: ['product_id', 'product_name', 'brand', 'url'],
    },
    {
      name: 'Column Sort - Brand Settings',
      description: 'Brand user sorts the product list by a column header.',
      interaction_context: 'product_list',
      interaction_element: 'column_sort',
      interaction_feature: 'brand_product_retirement',
      selected_values: ['product', 'retirement_date', 'replacement'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Page Select - Brand Settings',
      description:
        'Brand user navigates to a page in the product list pagination.',
      interaction_context: 'product_list',
      interaction_element: 'page_select',
      interaction_feature: 'brand_product_retirement',
      selected_values: 'dynamic — page number',
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Save - Brand Settings',
      description:
        "Brand user clicks 'Save' to persist product retirement configuration. A separate brand_product_retirement_saved event fires with the full batch summary.",
      interaction_context: 'product_retirement',
      interaction_element: 'save',
      interaction_feature: 'brand_product_retirement',
      expected_properties: ['url'],
    },
    {
      name: 'Recipient Select - Brand Contact Preferences',
      description:
        'Brand user selects a recipient type for contact requests (Default rep or General Contact inbox).',
      interaction_context: 'contact_preferences',
      interaction_element: 'recipient_select',
      interaction_feature: 'brand_contact_preferences',
      selected_values: ['default', 'general_contact'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Lead Time Toggle - Brand Contact Preferences',
      description:
        "Brand user toggles the 'Lead time' contact option on or off.",
      interaction_context: 'contact_options',
      interaction_element: 'lead_time_toggle',
      interaction_feature: 'brand_contact_preferences',
      selected_values: ['enabled', 'disabled'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Pricing Quote Toggle - Brand Contact Preferences',
      description:
        "Brand user toggles the 'Pricing / quote' contact option on or off.",
      interaction_context: 'contact_options',
      interaction_element: 'pricing_quote_toggle',
      interaction_feature: 'brand_contact_preferences',
      selected_values: ['enabled', 'disabled'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Something Else Toggle - Brand Contact Preferences',
      description:
        "Brand user toggles the 'Something else' contact option on or off.",
      interaction_context: 'contact_options',
      interaction_element: 'something_else_toggle',
      interaction_feature: 'brand_contact_preferences',
      selected_values: ['enabled', 'disabled'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Text Toggle - Brand Contact Preferences',
      description: "Brand user toggles the 'Text' contact option on or off.",
      interaction_context: 'contact_options',
      interaction_element: 'text_toggle',
      interaction_feature: 'brand_contact_preferences',
      selected_values: ['enabled', 'disabled'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Call Toggle - Brand Contact Preferences',
      description: "Brand user toggles the 'Call' contact option on or off.",
      interaction_context: 'contact_options',
      interaction_element: 'call_toggle',
      interaction_feature: 'brand_contact_preferences',
      selected_values: ['enabled', 'disabled'],
      expected_properties: ['selected_value', 'url'],
    },
    {
      name: 'Save - Brand Contact Preferences',
      description:
        "Brand user clicks 'Save' to persist contact preference configuration. A separate brand_contact_preferences_saved event fires with the full configuration state.",
      interaction_context: 'contact_preferences',
      interaction_element: 'save',
      interaction_feature: 'brand_contact_preferences',
      expected_properties: ['url'],
    },
  ],
}
